/**
 * PanelConsultas — Hablarle a un agente desde el tablero (v3, 31-ago-2026).
 *
 * Hasta hoy preguntarle algo a un agente exigía abrir su tmux. Esto es el canal.
 *
 * LO QUE LA PANTALLA TIENE QUE DEJAR CLARO es que esto NO es un chat: entre que se manda
 * la consulta y el agente la ve pasa hasta un minuto —la entrega el latido, no el ERP— y
 * la respuesta llega cuando el agente termine lo que estaba haciendo. Por eso los tres
 * estados se muestran con su nombre y no como un "enviando…": "pendiente" quiere decir
 * que el agente todavía no la vio, y eso es información, no una demora que haya que
 * disimular.
 */
import React, { useState } from "react";
import backend from "../../backend";

const C = { linea: "#2a2a2a", tenue: "#8a8a8a", texto: "#ddd" };

const ESTADO = {
  pendiente:  { texto: "en cola",   color: "#8a8a8a", ayuda: "el agente todavía no la vio" },
  entregada:  { texto: "entregada", color: "#3498db", ayuda: "ya está en su pantalla" },
  respondida: { texto: "respondida", color: "#2ecc71", ayuda: "" },
};

const cuando = iso =>
  iso ? new Date(iso).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const PanelConsultas = ({ agente, consultas = [], onEnviada }) => {
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  const mias = consultas.filter(c => c.agente === agente);

  const enviar = async () => {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    setError("");
    try {
      await backend.post("cuadrilla/consulta", { agente, texto: t });
      setTexto("");
      onEnviada?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.linea}` }}>
      <div style={{ fontSize: 10, color: "#7f8c8d", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>
        Consultas
      </div>
      {/* El aviso va SIEMPRE y no sólo cuando la lista está vacía: es la expectativa que
          hay que fijar, y si desaparece en cuanto hay una consulta, desaparece justo
          cuando alguien está esperando respuesta. */}
      <div style={{ fontSize: 10, color: C.tenue, marginBottom: 6 }}>
        La entrega la hace el latido: puede tardar hasta un minuto en llegarle, y responde cuando
        termine lo que está haciendo.
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <input
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") enviar(); }}
          placeholder={`Preguntarle algo a ${agente}…`}
          style={{ flex: 1, background: "#111", border: `1px solid ${C.linea}`, borderRadius: 5,
                   padding: "5px 9px", color: C.texto, fontSize: 12 }}
        />
        <button type="button" onClick={enviar} disabled={enviando || !texto.trim()}
          style={{ background: "#3498db22", color: "#3498db", border: "1px solid #3498db55",
                   borderRadius: 5, padding: "5px 12px", fontSize: 11, fontWeight: 700,
                   cursor: enviando ? "default" : "pointer" }}>
          {enviando ? "…" : "Enviar"}
        </button>
      </div>

      {error && <div style={{ fontSize: 11, color: "#e74c3c", marginBottom: 6 }}>⚠ {error}</div>}

      {!mias.length ? (
        <div style={{ fontSize: 11, color: C.tenue, fontStyle: "italic" }}>Sin consultas.</div>
      ) : (
        mias.slice(0, 6).map(c => {
          const meta = ESTADO[c.estado] || ESTADO.pendiente;
          return (
            <div key={c._id} style={{ borderTop: "1px solid #1f1f1f", padding: "6px 0" }}>
              <div style={{ display: "flex", gap: 7, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
                               background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}>
                  {meta.texto}
                </span>
                <span style={{ fontSize: 11, color: C.texto, flex: 1, minWidth: 0 }}>{c.texto}</span>
                <span style={{ fontSize: 10, color: C.tenue }}>{cuando(c.createdAt)}</span>
              </div>
              {c.estado === "respondida" ? (
                <div style={{ fontSize: 11, color: "#b8e6c8", marginTop: 3, paddingLeft: 10,
                              borderLeft: "2px solid #2ecc7155" }}>
                  {c.respuesta}
                </div>
              ) : (
                meta.ayuda && <div style={{ fontSize: 10, color: C.tenue, marginTop: 2 }}>{meta.ayuda}</div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PanelConsultas;
